import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { TimelineEventData } from '@/types/api';

const KEY = (clientId: number) => ['timeline', clientId];

export function useTimeline(clientId: number | undefined) {
  return useQuery({
    queryKey: clientId == null ? ['timeline', null] : KEY(clientId),
    enabled: clientId != null,
    queryFn: async (): Promise<TimelineEventData[]> => {
      const { data } = await api.get<TimelineEventData[]>(`/v1/clients/${clientId}/timeline`);
      return data;
    },
  });
}

export type TimelineEventInput = {
  type: string;
  direction?: string;
  title?: string;
  body?: string;
  files?: File[];
};

export function useCreateTimelineEvent(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TimelineEventInput): Promise<TimelineEventData> => {
      await primeCsrf();
      const fd = new FormData();
      fd.append('type', input.type);
      if (input.direction) fd.append('direction', input.direction);
      if (input.title) fd.append('title', input.title);
      if (input.body) fd.append('body', input.body);
      (input.files ?? []).forEach((f) => fd.append('files[]', f));

      const { data } = await api.post<TimelineEventData>(
        `/v1/clients/${clientId}/timeline`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}

/**
 * Remove an entry someone logged by hand. The server accepts only `manual`
 * entries: everything else on the timeline is a record of something that
 * happened and is owned by whatever wrote it.
 */
export function useDeleteTimelineEvent(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/timeline/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}
